'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { trackEvent } from '@/lib/analytics';

export default function ContactSection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const isValid = name?.trim()?.length > 0 && email?.trim()?.length > 0 && message?.trim()?.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setStatus('loading');
    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ name, email, company, message }),
      });

      if (!res.ok) {
        throw new Error('Edge function returned an error');
      }

      trackEvent('form_submit', {
        form_id: 'contact_form',
        has_company: company?.trim()?.length > 0,
      });

      setStatus('success');
      setName('');
      setEmail('');
      setCompany('');
      setMessage('');
    } catch {
      trackEvent('form_error', { form_id: 'contact_form' });
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-blackout relative overflow-hidden" ref={sectionRef}>
      {/* Background glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gold/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 reveal">
          <span className="text-gold text-xs font-semibold uppercase tracking-[0.3em] mb-3 sm:mb-4 block">{t('contact.label')}</span>
          <h2 className="text-3xl sm:text-4xl md:text-display font-black text-monitor mb-3 sm:mb-4">{t('contact.title')}</h2>
          <p className="text-monitor/40 text-sm sm:text-base leading-relaxed px-2 sm:px-0">
            {t('contact.description')}
          </p>
        </div>

        {/* Form card */}
        <div className="reveal border border-white/5 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 bg-charcoal/20 backdrop-blur-sm mx-2 sm:mx-0">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-monitor font-black text-lg mb-2">{t('contact.successTitle')}</h3>
              <p className="text-monitor/40 text-sm">{t('contact.successMessage')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-monitor/40 mb-2">
                  {t('contact.nameLabel')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e?.target?.value)}
                  placeholder={t('contact.namePlaceholder')}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-4 py-3 sm:py-3.5 text-monitor placeholder-monitor/20 text-sm focus:outline-none focus:border-gold/40 focus:bg-white/8 transition-all duration-300"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-monitor/40 mb-2">
                  {t('contact.emailLabel')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder={t('contact.emailPlaceholder')}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-4 py-3 sm:py-3.5 text-monitor placeholder-monitor/20 text-sm focus:outline-none focus:border-gold/40 focus:bg-white/8 transition-all duration-300"
                />
              </div>

              {/* Company (optional) */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-monitor/40 mb-2">
                  {t('contact.companyLabel')}
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e?.target?.value)}
                  placeholder={t('contact.companyPlaceholder')}
                  className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-4 py-3 sm:py-3.5 text-monitor placeholder-monitor/20 text-sm focus:outline-none focus:border-gold/40 focus:bg-white/8 transition-all duration-300"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-monitor/40 mb-2">
                  {t('contact.messageLabel')}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e?.target?.value)}
                  placeholder={t('contact.messagePlaceholder')}
                  rows={5}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg sm:rounded-xl px-4 py-3 sm:py-3.5 text-monitor placeholder-monitor/20 text-sm focus:outline-none focus:border-gold/40 focus:bg-white/8 transition-all duration-300 resize-none"
                />
              </div>

              {/* Error message */}
              {status === 'error' && (
                <p className="text-red-400 text-xs text-center">{t('contact.errorMessage')}</p>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={!isValid || status === 'loading'}
                className={`w-full flex items-center justify-center gap-3 py-3 sm:py-4 rounded-lg sm:rounded-xl font-black text-xs sm:text-sm uppercase tracking-widest transition-all duration-300 ${
                  isValid && status !== 'loading' ?'gold-shimmer-btn text-blackout hover:shadow-gold-glow cursor-pointer' :'bg-white/5 text-monitor/20 cursor-not-allowed border border-white/5'
                }`}
              >
                {status === 'loading' ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <>
                    <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {t('contact.sendButton')}
                  </>
                )}
              </button>

              {/* Reply note */}
              <p className="text-center text-gold/50 text-xs font-medium tracking-wide">
                ⏱ {t('contact.replyNote')}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
